import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import Price from '@/components/ui/Price';
import { getStripePromise } from './stripeClient';

interface Props {
  restaurantUuid: string;
  totalCents: number;
  currency: string;
  placeOrder: () => Promise<{ clientSecret: string; orderId: string; orderAccessToken?: string | null } | null>;
  buildReturnUrl: (orderId: string, orderAccessToken?: string | null) => string;
  onSuccess: (orderId: string, orderAccessToken?: string | null) => void;
  onError: (message: string) => void;
  disabled?: boolean;
}

export default function PaymentElementSection(props: Props) {
  return (
    <Elements
      stripe={getStripePromise(props.restaurantUuid)}
      options={{
        mode: 'payment',
        amount: props.totalCents,
        currency: props.currency,
        // Omitting paymentMethodTypes lets PaymentElement surface wallets
        // (Apple Pay / Google Pay / Link) when supported by the device.
      }}
    >
      <PayForm {...props} />
    </Elements>
  );
}

function PayForm({
  totalCents,
  placeOrder,
  buildReturnUrl,
  onSuccess,
  onError,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  // Keep Stripe's deferred-intent amount in sync when the cart total shifts
  // (e.g., the user toggles takeaway↔delivery and the delivery fee changes).
  useEffect(() => {
    if (!elements) return;
    elements.update({ amount: totalCents });
  }, [elements, totalCents]);

  const onPay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const submitResult = await elements.submit();
      if (submitResult?.error) return; // Stripe renders field-level error inline

      const intent = await placeOrder();
      if (!intent) return; // parent has already toasted

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: intent.clientSecret,
        confirmParams: { return_url: buildReturnUrl(intent.orderId, intent.orderAccessToken) },
        redirect: 'if_required',
      });
      if (error) {
        onError(error.message ?? t('payment.failed'));
        return;
      }
      if (paymentIntent?.status === 'succeeded') {
        onSuccess(intent.orderId, intent.orderAccessToken);
      } else {
        onError(t('payment.notConfirmed'));
      }
    } catch {
      onError(t('payment.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <PaymentElement options={{ layout: 'tabs' }} />
      <button
        type="button"
        onClick={onPay}
        disabled={!stripe || !elements || submitting || Boolean(disabled)}
        className="w-full bg-mint border-2 border-ink rounded-btn px-6 py-3 font-display shadow-stamp-sm disabled:opacity-50"
      >
        {submitting ? '...' : (
          <>{t('payment.pay')} <Price amountCents={totalCents} /></>
        )}
      </button>
    </div>
  );
}
